import { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { AlertTriangle } from 'lucide-react';

const ConfirmContext = createContext(null);

/**
 * useConfirm() returns an async function: confirm(options) -> Promise<boolean>.
 * options: { title, description, confirmText, cancelText, requireType, typeWord, destructive }
 * Set requireType:true to force the user to type the typeWord (default "LÖSCHEN")
 * before the confirm button becomes active — used for critical deletions.
 */
export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
};

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const [typed, setTyped] = useState('');
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setTyped('');
    setState({
      title: opts.title || 'Wirklich löschen?',
      description: opts.description || 'Dieser Vorgang kann nicht rückgängig gemacht werden.',
      confirmText: opts.confirmText || 'Löschen',
      cancelText: opts.cancelText || 'Abbrechen',
      requireType: !!opts.requireType,
      typeWord: opts.typeWord || 'LÖSCHEN',
      destructive: opts.destructive !== false,
    });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const close = (result) => {
    if (resolver.current) { resolver.current(result); resolver.current = null; }
    setState(null);
    setTyped('');
  };

  const canConfirm = !state?.requireType || typed.trim().toUpperCase() === (state?.typeWord || '').toUpperCase();

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {state?.destructive && <AlertTriangle className="h-5 w-5 text-rose-500 shrink-0" />}
              {state?.title}
            </AlertDialogTitle>
            <AlertDialogDescription>{state?.description}</AlertDialogDescription>
          </AlertDialogHeader>

          {state?.requireType && (
            <div className="space-y-1.5">
              <p className="text-sm text-muted-foreground">
                Gib zur Bestätigung <span className="font-semibold text-foreground">{state.typeWord}</span> ein:
              </p>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={state.typeWord}
                data-testid="confirm-type-input"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm) close(true); }}
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => close(false)} data-testid="confirm-cancel">
              {state?.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!canConfirm}
              onClick={() => close(true)}
              data-testid="confirm-accept"
              className={state?.destructive ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
            >
              {state?.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};
